/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import Dialog from '@material-ui/core/Dialog';
import Button from 'components/common/Button';
import DialogStatus from 'layout/dialog/DialogStatus';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Typography from '@material-ui/core/Typography';
import { useTranslation } from 'react-i18next';
import { notificationType } from '../../shared/enums/NotificationType';

export default function HandleInvitationDialog({
  invitation,
  invitationStatus,
  dialog,
  toggleDialog,
  onSubmit,
  onAcceptedInvitationConfirm,
}) {
  const { t } = useTranslation();

  const { id, organizationName } = invitation || {};

  const renderInvitationTitle = () => {
    switch (invitation.type) {
      case notificationType.JOIN_ORGANIZATION:
        return t('user-management.accept.invitation.dialog.title');
      case notificationType.MERGE_USER_ACCOUNT:
        return (
          <>
            <DialogStatus variant="warning" />
            {t('user-management.merge.invite.accept.title')}
          </>
        );
      default:
        return null;
    }
  };

  const renderInvitationContent = () => {
    switch (invitation.type) {
      case notificationType.JOIN_ORGANIZATION:
        return t('user-management.accept.invitation.dialog.description', {
          organisationName: organizationName,
        });
      case notificationType.MERGE_USER_ACCOUNT:
        return t('user-management.merge.invite.accept.description');
      default:
        return null;
    }
  };

  const renderInvitationActionLabel = () => {
    switch (invitation.type) {
      case notificationType.JOIN_ORGANIZATION:
        return t('user-management.accept.invitation');
      case notificationType.MERGE_USER_ACCOUNT:
        return t('user-management.merge.invite.accept.action');
      default:
        return null;
    }
  };

  const renderConfirmationTitle = () => {
    switch (invitation.type) {
      case notificationType.JOIN_ORGANIZATION:
        return (
          <>
            <DialogStatus variant="success" />
            {t('user-management.confirm.invitation.join.success.title')}
          </>
        );
      case notificationType.MERGE_USER_ACCOUNT:
        return (
          <>
            <DialogStatus variant="success" />
            {t('user-management.confirm.invitation.merge.success.title')}
          </>
        );
      default:
        return null;
    }
  };

  const renderConfirmationContent = () => {
    switch (invitation.type) {
      case notificationType.JOIN_ORGANIZATION:
        return t('user-management.confirm.invitation.join.success.description');
      case notificationType.MERGE_USER_ACCOUNT:
        return t('user-management.confirm.invitation.merge.success.description');
      default:
        return null;
    }
  };

  const renderConfirmationActionLabel = () => {
    switch (invitation.type) {
      case notificationType.JOIN_ORGANIZATION:
        return t('user-management.confirm.invitation.join.success.button');
      case notificationType.MERGE_USER_ACCOUNT:
        return t('user-management.confirm.invitation.merge.success.button');
      default:
        return null;
    }
  };

  return (
    <Dialog data-testid="handle-invite-dialog" open={dialog.open} fullWidth maxWidth="md">
      {invitationStatus === 'accepted' ? (
        <>
          <DialogTitle>
            <Typography component="span" variant="h2">
              {renderConfirmationTitle()}
            </Typography>
          </DialogTitle>
          <DialogContent>{renderConfirmationContent()}</DialogContent>
          <DialogActions>
            <Button
              data-testid="confirm-dialog"
              onClick={() => {
                onAcceptedInvitationConfirm();
              }}
              color="primary"
            >
              {renderConfirmationActionLabel()}
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle>
            <Typography component="span" variant="h2">
              {renderInvitationTitle()}
            </Typography>
          </DialogTitle>
          <DialogContent>{renderInvitationContent()}</DialogContent>
          <DialogActions>
            <Button data-testid="cancel-action" color="default" onClick={() => toggleDialog(false)}>
              {t('actions.cancel')}
            </Button>
            <Button
              data-testid="accept-action"
              onClick={() => {
                onSubmit(id);
              }}
              color="primary"
            >
              {renderInvitationActionLabel()}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
